import React, { useState, useEffect, useContext } from 'react';
import axios from '../api/axiosConfig';
import { AuthContext } from '../context/AuthContext';
import '../pages/Profiles.css';
import FormularioInscripcionPrenda from './FormularioInscripcionPrenda';
import FormularioDuplicadoLicencia from './FormularioDuplicadoLicencia';
import FormularioTraspaso from './FormularioTraspaso';

const NuevoTramiteModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useContext(AuthContext);
  const [transitos, setTransitos] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [selectedZona, setSelectedZona] = useState('');
  const [tramiteTypes, setTramiteTypes] = useState([]);
  const [plates, setPlates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPlateDropdown, setShowPlateDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPlates, setLoadingPlates] = useState(false);
  const [searchingOwner, setSearchingOwner] = useState(false);
  const [ownerChecked, setOwnerChecked] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  // eslint-disable-next-line no-unused-vars
  const [extractedFromInvoice, setExtractedFromInvoice] = useState(null);

  const [searchingAcreedor, setSearchingAcreedor] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [acreedorChecked, setAcreedorChecked] = useState(false);

  const [formData, setFormData] = useState({
        transito_id: '',
        tipo_tramite_id: '',
        vehicle_type: '',
        propietario: '',
        tipo_documento_id: 'CC',
        documento: '',
        correo: '',
        telefono: '',
        direccion: '',
        placa_referencia: '',
        observaciones: '',
        chasis: '',
        motor: '',
        vin: '',
        vehicleDetails: null,
        estado_persona: null,
        tiene_multas: false,
        acreedor_tipo_documento: 'NIT',
        acreedor_documento: '',
        acreedor_nombre: '',
        acreedor_estado_persona: null,
        nuevo_color: ''
    });

  const [files, setFiles] = useState({
    cedula: null,
    factura: null,
    declaracion_importacion: null,
    contrato_prenda: null
  });

  const [transitoConfig, setTransitoConfig] = useState(null);

  const selectedTramiteType = tramiteTypes.find(tt => tt.id === parseInt(formData.tipo_tramite_id));
  const isPrenda = selectedTramiteType ? selectedTramiteType.name.toUpperCase().includes('PRENDA') : false;
  const isInscPrenda = selectedTramiteType
    ? selectedTramiteType.name.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim() === 'INSCRIPCION DE PRENDA'
    : false;
  const isDupLic = selectedTramiteType
    ? selectedTramiteType.name.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/^TRAMITE\s+/, '').trim() === 'DUPLICADO LICENCIA TRANSITO'
    : false;
  // Traspaso / traspaso con levante de prenda: flujo propio solo en los tránsitos habilitados (Zona Bananera)
  const nombreTipoNorm = selectedTramiteType ? selectedTramiteType.name.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/^TRAMITE\s+/, '').trim() : '';
  const transitoSel = transitos.find(t => String(t.id) === String(formData.transito_id));
  const traspasoSubtipo = (String(transitoSel?.name || '').toUpperCase().includes('ZONA BANANERA'))
    ? (nombreTipoNorm === 'TRASPASO' ? 'traspaso' : (nombreTipoNorm === 'TRASPASO CON LEVANTE DE PRENDA' ? 'traspaso_levante_prenda' : null))
    : null;
  const isTraspaso = !!traspasoSubtipo;
  const isCambioColor = selectedTramiteType ? selectedTramiteType.name.toUpperCase().includes('COLOR') : false;

  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
      
      // Auto-seleccionar zona si el asesor solo tiene 1
      const isSuperOrAdmin = user?.role === 'superadmin' || user?.profile_name === 'admin_concesionario' || user?.role === 'admin';
      if (!isSuperOrAdmin && user?.zonas_asignadas?.length === 1) {
        setSelectedZona(user.zonas_asignadas[0].id.toString());
      }
    } else {
      resetForm();
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (formData.transito_id) {
      const selectedTransito = transitos.find(t => t.id === parseInt(formData.transito_id));
      setTransitoConfig(selectedTransito);
      
      if (selectedTransito?.plate_assignment_mode === 'automatico') {
        // En modo automático, intentamos mostrar cuál sería la siguiente placa
        const availablePlatesForType = plates.filter(p => p.vehicle_type === formData.vehicle_type && p.status === 'disponible');
        const nextPlate = availablePlatesForType.length > 0 ? availablePlatesForType[0].plate_number : 'PENDIENTE';
        
        if (formData.vehicle_type) {
          setSearchTerm(`AUTO: ${nextPlate}`);
          setFormData(prev => ({ ...prev, placa_referencia: 'AUTO' }));
        } else {
          setSearchTerm('SELECCIONE TIPO DE VEHÍCULO');
          setFormData(prev => ({ ...prev, placa_referencia: '' }));
        }
      } else {
        setSearchTerm('');
        setFormData(prev => ({ ...prev, placa_referencia: '' }));
      }
    } else {
      setTransitoConfig(null);
    }
  }, [formData.transito_id, formData.vehicle_type, transitos, plates]);

  const fetchInitialData = async () => {
    try {
      const [transRes, typesRes, zonasRes] = await Promise.all([
        axios.get('/api/transitos'),
        axios.get('/api/tramite-types'),
        axios.get('/api/zonas')
      ]);
      setTransitos(transRes.data);
      setTramiteTypes(typesRes.data);
      setZonas(zonasRes.data);
    } catch (err) {
      console.error('Error al cargar datos iniciales:', err);
    }
  };

  useEffect(() => {
    // Evita que una respuesta tardía de un tránsito anterior (o de un trámite anterior)
    // sobrescriba las placas del tránsito actualmente seleccionado.
    let cancelled = false;
    const transitoId = formData.transito_id;
    setPlates([]);
    const fetchPlates = async () => {
      if (transitoId) {
        try {
          setLoadingPlates(true);
          const url = formData.vehicle_type 
            ? `/api/transitos/${transitoId}/plates?vehicle_type=${formData.vehicle_type}`
            : `/api/transitos/${transitoId}/plates`;
          const res = await axios.get(url);
          if (cancelled) return;
          setPlates(res.data.filter(p => p.status === 'disponible' && String(p.transito_id) === String(transitoId)));
        } catch (err) {
          if (cancelled) return;
          console.error('Error al cargar placas:', err);
          setPlates([]);
        } finally {
          if (!cancelled) setLoadingPlates(false);
        }
      } else {
        setLoadingPlates(false);
      }
    };
    fetchPlates();
    return () => { cancelled = true; };
  }, [formData.transito_id, formData.vehicle_type, isOpen]);

  const resetForm = () => {
    setFormData({
      transito_id: '',
      tipo_tramite_id: '',
      vehicle_type: '',
      propietario: '',
      tipo_documento_id: 'CC',
      documento: '',
      correo: '',
      telefono: '',
      direccion: '',
      placa_referencia: '',
      observaciones: '',
      chasis: '',
      motor: '',
      vin: '',
      vehicleDetails: null,
      estado_persona: null,
      tiene_multas: false,
      acreedor_tipo_documento: 'NIT',
      acreedor_documento: '',
      acreedor_nombre: '',
      acreedor_estado_persona: null,
      nuevo_color: ''
    });
    setFiles({ cedula: null, factura: null, declaracion_importacion: null, contrato_prenda: null });
    setSearchTerm('');
    setSelectedZona('');
    setOwnerChecked(false);
    setAcreedorChecked(false);
    setMessage({ text: '', type: '' });
    setExtractedFromInvoice(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const upperValue = (name !== 'transito_id' && name !== 'tipo_tramite_id' && name !== 'tipo_documento_id' && name !== 'acreedor_tipo_documento' && name !== 'correo' && name !== 'vehicle_type') 
      ? value.toUpperCase() 
      : value;

    setFormData({ ...formData, [name]: upperValue });
    
    if (name === 'transito_id') {
      setFormData(prev => ({ ...prev, transito_id: value, placa_referencia: '', tipo_tramite_id: '' }));
      setSearchTerm('');
    }
    if (name === 'documento' || name === 'tipo_documento_id') {
      setOwnerChecked(false);
      setMessage({ text: '', type: '' });
    }
    if (name === 'acreedor_documento' || name === 'acreedor_tipo_documento') {
      setAcreedorChecked(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // VALIDAR EXTENSIÓN DEL ARCHIVO
    const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
    const fileExt = file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(fileExt)) {
      alert(`❌ TIPO DE ARCHIVO NO PERMITIDO\n\nEl archivo "${file.name}" es un archivo de formato .${fileExt}.\n\nPor favor, sube ÚNICAMENTE archivos PDF o Imágenes.\n\n⚠️ No se aceptan archivos de Word (.doc, .docx) ni Excel.`);
      e.target.value = '';
      return;
    }
    
    if (file.size > 6 * 1024 * 1024) {
      alert(`❌ El archivo ${file.name} supera los 6MB`);
      e.target.value = '';
      return;
    }
    
    // Actualizamos los archivos primero
    setFiles({ ...files, [e.target.name]: file });
    
    // Si es la factura, extraemos datos inmediatamente
    if (e.target.name === 'factura') {
      setMessage({ text: '⏳ Extrayendo datos de la factura...', type: 'info' });
      try {
        const formData = new FormData();
        formData.append('factura', file);
        
        const res = await axios.post('/api/tramites/extract-pdf', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (res.data) {
          setExtractedFromInvoice(res.data);
          // Autocompletar campos
          setFormData(prev => ({
            ...prev,
            chasis: res.data.Chasis || prev.chasis,
            motor: res.data.Motor || prev.motor,
            vin: res.data.VIN || prev.vin,
            // Contacto del comprador tomado de la factura (solo si el campo está vacío)
            correo: prev.correo || (res.data.Cliente_Correo || '').toLowerCase(),
            telefono: prev.telefono || res.data.Cliente_Telefono || '',
            direccion: prev.direccion || (res.data.Cliente_Direccion || '').toUpperCase()
          }));

          if (!res.data.Chasis && !res.data.Motor && !res.data.VIN) {
            alert('⚠️ No se detectaron datos (Chasis/Motor/VIN) en el archivo subido.\n\n¿Estás segur@ que esto es una factura?');
            setMessage({ text: '⚠️ Datos no detectados. Verifica que el archivo sea una factura válida.', type: 'error' });
          } else {
            setMessage({ text: '✅ Datos extraídos correctamente!', type: 'success' });
          }

          // Validación de Zona para Todomotos
          try {
            let activeZonaId = selectedZona;
            if (!activeZonaId && formData.transito_id) {
               const transitoObj = transitos.find(t => t.id.toString() === formData.transito_id.toString());
               if (transitoObj) activeZonaId = transitoObj.zona_id;
            }
            if (!activeZonaId && user?.zonas_asignadas?.length === 1) {
               activeZonaId = user.zonas_asignadas[0].id.toString();
            }
            
            const currentZona = activeZonaId ? zonas.find(z => z.id.toString() === activeZonaId.toString()) : null;
            if (currentZona) {
              const zonaName = currentZona.name.toUpperCase();
              const puntoVenta = (res.data.Punto_Venta || '').toUpperCase();
              const vendedor = (res.data.Vendedor || '').toUpperCase();
              const textToCheck = `${puntoVenta} ${vendedor}`;
              
              if (textToCheck.includes('TODOMOTOS')) {
                const keywordsMagdalena = ['SANTA MARTA', 'BASTIDAS', 'LA QUINTA', 'LA LUCHA', 'RODADERO', 'CIENAGA', 'ORIHUECA', 'MAGDALENA'];
                const keywordsAtlantico = ['B/QUILLA', 'BARRANQUILLA', 'CORDIALIDAD', 'SAN MARTIN', 'BARANOA', 'PUERTO COLOMBIA', 'LA 21', 'ATLANTICO'];

                const isMagdalena = keywordsMagdalena.some(kw => textToCheck.includes(kw));
                const isAtlantico = keywordsAtlantico.some(kw => textToCheck.includes(kw));

                if ((zonaName.includes('ARACATACA') || zonaName.includes('ZONA BANANERA')) && isAtlantico && !isMagdalena) {
                  alert(`⚠️ ADVERTENCIA DE ZONA:\n\nLa factura de Todomotos pertenece a una sede de ATLÁNTICO (${puntoVenta || vendedor || 'No identificada'}), pero la Zona seleccionada es ${currentZona.name}.\n\nPor favor, verifique el Tránsito y Zona elegidos antes de continuar.`);
                } else if ((zonaName.includes('SOLEDAD') || zonaName.includes('SABANALARGA')) && isMagdalena && !isAtlantico) {
                  alert(`⚠️ ADVERTENCIA DE ZONA:\n\nLa factura de Todomotos pertenece a una sede de MAGDALENA (${puntoVenta || vendedor || 'No identificada'}), pero la Zona seleccionada es ${currentZona.name}.\n\nPor favor, verifique el Tránsito y Zona elegidos antes de continuar.`);
                }
              }
            }
          } catch (e) {
            console.error('Error validando zona de todomotos:', e);
          }
        }
      } catch (err) {
        console.error('Error al extraer datos:', err);
        alert('⚠️ No se pudieron extraer datos de la factura');
      }
    }
  };

  const searchOwner = async () => {
    if (!formData.documento) {
      alert('⚠️ Ingrese un número de documento para buscar');
      return;
    }

    try {
      setSearchingOwner(true);
      setMessage({ text: '', type: '' });
      const res = await axios.get(`/api/tramites/owner/${formData.documento}?tipo_documento_id=${formData.tipo_documento_id}`);
      
      if (res.data) {
        const tieneMultas = Boolean(res.data.tiene_multas);
        const estado = res.data.estado_persona;

        if (tieneMultas) {
          const confirmContinue = window.confirm(
            `🚨 ALERTA DE MULTAS / SIMIT:\n\n` +
            `El ciudadano ${res.data.propietario || ''} (${formData.tipo_documento_id}: ${formData.documento}) presenta multas o comparendos pendientes en SIMIT.\n\n` +
            `Al radicar el trámite se generará automáticamente una novedad asignada al Concesionario.\n\n` +
            `¿Desea continuar creando el trámite?`
          );

          if (!confirmContinue) {
            setMessage({
              text: '❌ Creación de trámite cancelada debido a las multas o comparendos pendientes del ciudadano.',
              type: 'error'
            });
            setOwnerChecked(false);
            setSearchingOwner(false);
            return;
          }
        }

        setFormData(prev => ({
          ...prev,
          propietario: res.data.propietario || '',
          tipo_documento_id: res.data.tipo_documento_id || 'CC',
          correo: res.data.correo || prev.correo || '',
          telefono: res.data.telefono || prev.telefono || '',
          direccion: res.data.direccion || prev.direccion || '',
          estado_persona: res.data.estado_persona || null,
          tiene_multas: tieneMultas
        }));
        
        let warningText = '';
        if (!estado || !estado.toUpperCase().includes('ACTIV')) {
          warningText += `⚠️ ATENCIÓN: El ciudadano NO se encuentra ACTIVO en el RUNT (Estado actual: ${estado || 'NO INSCRITO'}). `;
        }
        if (tieneMultas) {
          warningText += `🚨 ALERTA SIMIT: El ciudadano presenta multas/comparendos pendientes. Al radicar el trámite se generará automáticamente una novedad asignada al Concesionario.`;
        }

        if (warningText) {
          alert(warningText);
          setMessage({ text: warningText, type: 'info' });
        } else {
          setMessage({ text: '✅ Información del cliente cargada correctamente. Sin multas reportadas.', type: 'success' });
        }
      }
      setOwnerChecked(true);
    } catch (err) {
      console.error('Error al buscar cliente:', err);
      setOwnerChecked(true);
      if (err.response?.status === 404) {
        alert('ℹ️ Cliente no encontrado. Ingrese los datos manualmente.');
      } else if (err.response?.status === 403) {
        alert('❌ Acceso denegado a la API externa (posible límite de cupo excedido).');
      } else if (err.response?.status === 401) {
        alert('❌ No autorizado para consultar la API externa (token inválido).');
      } else {
        alert('❌ Error al buscar información del cliente');
      }
    } finally {
      setSearchingOwner(false);
    }
  };

  const searchAcreedor = async () => {
    if (!formData.acreedor_documento) {
      alert('⚠️ Ingrese el número de documento del acreedor prendario');
      return;
    }

    try {
      setSearchingAcreedor(true);
      setMessage({ text: '', type: '' });
      const res = await axios.get(`/api/tramites/owner/${formData.acreedor_documento}?tipo_documento_id=${formData.acreedor_tipo_documento}`);
      
      if (res.data) {
        setFormData(prev => ({
          ...prev,
          acreedor_nombre: res.data.propietario || '',
          acreedor_tipo_documento: res.data.tipo_documento_id || prev.acreedor_tipo_documento,
          acreedor_estado_persona: res.data.estado_persona || null
        }));
        
        const estado = res.data.estado_persona;
        if (!estado || !estado.toUpperCase().includes('ACTIV')) {
          alert('⚠️ ATENCIÓN: El acreedor prendario NO se encuentra ACTIVO en el RUNT. Estado actual: ' + (estado || 'NO INSCRITO'));
        } else {
          setMessage({ text: '✅ Información del acreedor prendario cargada correctamente', type: 'success' });
        }
      }
      setAcreedorChecked(true);
    } catch (err) {
      console.error('Error al buscar acreedor:', err);
      setAcreedorChecked(true);
      if (err.response?.status === 404) {
        alert('ℹ️ Acreedor no encontrado en sistema. Ingrese los datos manualmente.');
      } else {
        alert('❌ Error al buscar información del acreedor prendario');
      }
    } finally {
      setSearchingAcreedor(false);
    }
  };

  const handlePlateSelect = (plateNumber) => {
    setFormData({ ...formData, placa_referencia: plateNumber });
    setSearchTerm(plateNumber);
    setShowPlateDropdown(false);
  };

  const filteredPlates = plates.filter(p => {
    const matchesSearch = p.plate_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !formData.vehicle_type || p.vehicle_type === formData.vehicle_type;
    return matchesSearch && matchesType;
  });

  const filteredTramiteTypes = tramiteTypes.filter(tt => 
    formData.transito_id && tt.transito_id === parseInt(formData.transito_id) && tt.is_active !== false
  );

  const isSuperOrAdmin = user?.role === 'superadmin' || user?.profile_name === 'admin_concesionario' || user?.role === 'admin';
  const isMultiZonaUser = !isSuperOrAdmin && user?.zonas_asignadas && user.zonas_asignadas.length > 1;
  const requireZonaSelection = isSuperOrAdmin || isMultiZonaUser;

  const zonasAMostrar = isSuperOrAdmin ? zonas : (user?.zonas_asignadas || []);

  const filteredTransitos = transitos.filter(t => {
    if (t.status === 'inactivo') return false;
    if (requireZonaSelection) {
      return selectedZona ? t.zona_id === parseInt(selectedZona) : false;
    } else {
      // Para punto de venta con 1 sola zona
      const defaultZonaId = user?.zonas_asignadas?.[0]?.id;
      return defaultZonaId ? t.zona_id === defaultZonaId : false;
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isInscPrenda || isDupLic || isTraspaso) return;

    if (!formData.vehicle_type) {
      alert('⚠️ Debe seleccionar un tipo de vehículo');
      return;
    }

    if (isCambioColor && !formData.nuevo_color?.trim()) {
      alert('⚠️ Debe ingresar el nombre del nuevo color para trámites de cambio de color');
      return;
    }

    // Validar placa obligatoria en modo manual
    if (transitoConfig?.plate_assignment_mode === 'manual' && !formData.placa_referencia) {
      alert('⚠️ Debe seleccionar una placa de referencia');
      return;
    }
    
    const totalSize = Object.values(files).reduce((acc, file) => acc + (file?.size || 0), 0);
    if (totalSize > 15 * 1024 * 1024) {
      alert('❌ La suma total de los archivos no puede superar los 15MB');
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    const data = new FormData();
    data.append('transito_id', formData.transito_id);
    data.append('tipo_tramite_id', formData.tipo_tramite_id);
    data.append('vehicle_type', formData.vehicle_type);
    data.append('placa_referencia', formData.placa_referencia);
    data.append('datos_adicionales', JSON.stringify({
      propietario: formData.propietario,
      tipo_documento: formData.tipo_documento_id,
      representante_legal_nombre: formData.tipo_documento_id === 'NIT' ? formData.representante_legal_nombre : undefined,
      representante_legal_tipo_documento: formData.tipo_documento_id === 'NIT' ? (formData.representante_legal_tipo_documento || 'CC') : undefined,
      representante_legal_documento: formData.tipo_documento_id === 'NIT' ? formData.representante_legal_documento : undefined,
      documento: formData.documento,
      correo: formData.correo,
      telefono: formData.telefono,
      direccion: formData.direccion,
      vehicle_type: formData.vehicle_type,
      placa_referencia: formData.placa_referencia,
      observaciones: formData.observaciones,
      chasis: formData.chasis,
      motor: formData.motor,
      vin: formData.vin,
      vehicleDetails: formData.vehicleDetails,
      estado_persona: formData.estado_persona,
      tiene_multas: formData.tiene_multas,
      is_prenda: isPrenda,
      acreedor_tipo_documento: isPrenda ? formData.acreedor_tipo_documento : undefined,
      acreedor_documento: isPrenda ? formData.acreedor_documento : undefined,
      acreedor_nombre: isPrenda ? formData.acreedor_nombre : undefined,
      acreedor_estado_persona: isPrenda ? formData.acreedor_estado_persona : undefined,
      nuevo_color: isCambioColor ? formData.nuevo_color : undefined
    }));

    if (files.cedula) data.append('Cedula', files.cedula);
    if (files.factura) data.append('Factura', files.factura);
    if (files.declaracion_importacion) data.append('Declaracion_Importacion', files.declaracion_importacion);
    if (files.contrato_prenda) data.append('Contrato_Prenda', files.contrato_prenda);

    try {
      await axios.post('/api/tramites', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert('✅ Trámite iniciado correctamente');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err) {
      alert('❌ Error: ' + (err.response?.data?.message || 'No se pudo crear el trámite'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '800px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h3>📝 Iniciar Nuevo Trámite</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        
        {message.text && (
          <div className={`
            ${message.type === 'success' ? 'success-banner' : ''}
            ${message.type === 'error' ? 'error-banner' : ''}
            ${message.type === 'info' ? 'info-banner' : ''}
          `}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="user-form">
          <div className="form-section">
            <div className="form-section-header">
              <div className="form-section-icon">📋</div>
              <h4 className="form-section-title">DATOS DEL TRÁMITE</h4>
            </div>
            <div className="form-grid">
              {requireZonaSelection && (
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>ZONA <span style={{ color: 'red' }}>*</span></label>
                  <select value={selectedZona} onChange={(e) => {
                    setSelectedZona(e.target.value);
                    setFormData(prev => ({ ...prev, transito_id: '', tipo_tramite_id: '' }));
                  }} required>
                    <option value="">seleccione una zona...</option>
                    {zonasAMostrar.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>ORGANISMO DE TRÁNSITO <span style={{ color: 'red' }}>*</span></label>
                <select name="transito_id" value={formData.transito_id} onChange={handleInputChange} required disabled={requireZonaSelection && !selectedZona}>
                  <option value="">
                    {requireZonaSelection && !selectedZona ? 'seleccione primero una zona' : 'seleccione...'}
                  </option>
                  {filteredTransitos.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>TIPO DE TRÁMITE <span style={{ color: 'red' }}>*</span></label>
                <select name="tipo_tramite_id" value={formData.tipo_tramite_id} onChange={handleInputChange} required disabled={!formData.transito_id}>
                  <option value="">
                    {!formData.transito_id 
                      ? 'seleccione primero un tránsito' 
                      : filteredTramiteTypes.length === 0 
                        ? 'no hay trámites asociados a este tránsito' 
                        : 'seleccione...'}
                  </option>
                  {filteredTramiteTypes.map(tt => <option key={tt.id} value={tt.id}>{tt.name}</option>)}
                </select>
              </div>
            </div>

            {!(isInscPrenda || isDupLic || isTraspaso) && (
            <div className="form-group" style={{ marginTop: '15px' }}>
              <label>TIPO DE VEHÍCULO <span style={{ color: 'red' }}>*</span></label>
              <div style={{ display: 'flex', gap: '25px', marginTop: '10px', background: 'var(--secondary-soft)', padding: '12px', borderRadius: '8px', border: '1px solid var(--card-border)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  <input 
                    type="radio" 
                    name="vehicle_type"
                    value="carro"
                    checked={formData.vehicle_type === 'carro'} 
                    onChange={handleInputChange}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  🚗 CARRO
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  <input 
                    type="radio" 
                    name="vehicle_type"
                    value="moto"
                    checked={formData.vehicle_type === 'moto'} 
                    onChange={handleInputChange}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  MOTO
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  <input 
                    type="radio" 
                    name="vehicle_type"
                    value="moto_carro"
                    checked={formData.vehicle_type === 'moto_carro'} 
                    onChange={handleInputChange}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  MOTO CARRO
                </label>
              </div>
            </div>
            )}

            {isCambioColor && (
              <div className="form-group" style={{ marginTop: '15px' }}>
                <label>NUEVO COLOR DEL VEHÍCULO <span style={{ color: 'red' }}>*</span></label>
                <input 
                  type="text" 
                  name="nuevo_color" 
                  value={formData.nuevo_color} 
                  onChange={handleInputChange} 
                  required 
                  placeholder="Escriba el nombre del nuevo color (ej: ROJO, NEGRO MATE, AZUL Y BLANCO...)" 
                  style={{ textTransform: 'uppercase', fontWeight: 'bold', borderColor: '#2563eb' }}
                />
              </div>
            )}
          </div>

          {isInscPrenda && (
            <FormularioInscripcionPrenda onClose={onClose} onSuccess={onSuccess} />
          )}
          {isDupLic && (
            <FormularioDuplicadoLicencia onClose={onClose} onSuccess={onSuccess} />
          )}
          {isTraspaso && (
            <FormularioTraspaso key={traspasoSubtipo} subtipo={traspasoSubtipo} tipoTramiteId={formData.tipo_tramite_id} onClose={onClose} onSuccess={onSuccess} />
          )}

          {!(isInscPrenda || isDupLic || isTraspaso) && (<>
          <div className="form-section" style={!ownerChecked ? { borderLeft: '4px solid var(--primary-color)' } : {}}>
            <div className="form-section-header">
              <div className="form-section-icon">🔍</div>
              <h4 className="form-section-title">VERIFICACIÓN DE IDENTIDAD</h4>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', marginTop: '12px', gap: '12px' }}>
              <div className="form-group">
                <label>TIPO DOC. <span style={{ color: 'red' }}>*</span></label>
                <select name="tipo_documento_id" value={formData.tipo_documento_id} onChange={handleInputChange} required>
                  <option value="CC">cédula de ciudadanía</option>
                  <option value="NIT">nit</option>
                  <option value="CE">cédula de extranjería</option>
                  <option value="PP">pasaporte</option>
                  <option value="PPT">permiso de permanencia temporal (PPT)</option>
                  <option value="TI">tarjeta de identidad</option>
                </select>
              </div>
              <div className="form-group">
                <label>NRO DE DOCUMENTO <span style={{ color: 'red' }}>*</span></label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input 
                    type="text" 
                    name="documento" 
                    value={formData.documento} 
                    onChange={handleInputChange} 
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchOwner())}
                    required 
                    placeholder="número de documento" 
                    style={{ flex: 1 }}
                  />
                  <button 
                    type="button" 
                    onClick={searchOwner} 
                    className="btn-action btn-modules"
                    disabled={searchingOwner}
                    style={{ padding: '0 20px', height: '45px', fontWeight: '800' }}
                  >
                    {searchingOwner ? '⏳' : 'CONSULTAR'}
                  </button>
                </div>
              </div>
            </div>

            {formData.tipo_documento_id === 'NIT' && (
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.5fr', marginTop: '12px', gap: '12px' }}>
                <div className="form-group">
                  <label>REPRESENTANTE LEGAL <span style={{ color: 'red' }}>*</span></label>
                  <input type="text" name="representante_legal_nombre" value={formData.representante_legal_nombre || ''} onChange={handleInputChange} required placeholder="nombre completo" />
                </div>
                <div className="form-group">
                  <label>TIPO DOC. R.L.</label>
                  <select name="representante_legal_tipo_documento" value={formData.representante_legal_tipo_documento || 'CC'} onChange={handleInputChange}>
                    <option value="CC">CC</option>
                    <option value="CE">CE</option>
                    <option value="PP">PASAPORTE</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>DOCUMENTO R.L. <span style={{ color: 'red' }}>*</span></label>
                  <input type="text" name="representante_legal_documento" value={formData.representante_legal_documento || ''} onChange={handleInputChange} required placeholder="número" />
                </div>
              </div>
            )}

            {!ownerChecked && (
              <div className="modal-actions" style={{ marginTop: '0px' }}>
                <button type="button" onClick={onClose} className="btn-secondary">cancelar</button>
              </div>
            )}
          </div>

          {ownerChecked && (
            <>
              <div className="form-section">
                <div className="form-section-header">
                  <div className="form-section-icon">👤</div>
                  <h4 className="form-section-title">INFORMACIÓN DEL PROPIETARIO</h4>
                </div>
                
                <div className="form-group">
                  <label>NOMBRE COMPLETO <span style={{ color: 'red' }}>*</span></label>
                  <input type="text" name="propietario" value={formData.propietario} onChange={handleInputChange} required placeholder="nombre del propietario" />
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>CORREO ELECTRÓNICO</label>
                    <input type="email" name="correo" value={formData.correo} onChange={handleInputChange} placeholder="ejemplo@correo.com" />
                  </div>
                  <div className="form-group">
                    <label>TELÉFONO</label>
                    <input type="text" name="telefono" value={formData.telefono} onChange={handleInputChange} placeholder="número de contacto" />
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: '0' }}>
                  <label>DIRECCIÓN</label>
                  <input type="text" name="direccion" value={formData.direccion} onChange={handleInputChange} placeholder="dirección de residencia" />
                </div>
              </div>

              {isPrenda && (
                <div className="form-section">
                  <div className="form-section-header">
                    <div className="form-section-icon">🏦</div>
                    <h4 className="form-section-title">INFORMACIÓN DEL ACREEDOR PRENDARIO</h4>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', marginTop: '12px', gap: '12px' }}>
                    <div className="form-group">
                      <label>TIPO DOC. ACREEDOR <span style={{ color: '#94a3b8', fontSize: '11px' }}>(Opcional)</span></label>
                      <select name="acreedor_tipo_documento" value={formData.acreedor_tipo_documento} onChange={handleInputChange}>
                        <option value="NIT">NIT</option>
                        <option value="CC">CÉDULA DE CIUDADANÍA</option>
                        <option value="CE">CÉDULA DE EXTRANJERÍA</option>
                        <option value="PP">PASAPORTE</option>
                        <option value="PPT">PERMISO DE PERMANENCIA TEMPORAL (PPT)</option>
                        <option value="TI">TARJETA DE IDENTIDAD</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>NRO DE DOCUMENTO ACREEDOR <span style={{ color: '#94a3b8', fontSize: '11px' }}>(Opcional)</span></label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <input 
                          type="text" 
                          name="acreedor_documento" 
                          value={formData.acreedor_documento} 
                          onChange={handleInputChange} 
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchAcreedor())}
                          placeholder="número de documento del acreedor" 
                          style={{ flex: 1 }}
                        />
                        <button 
                          type="button" 
                          onClick={searchAcreedor} 
                          className="btn-action btn-modules"
                          disabled={searchingAcreedor}
                          style={{ padding: '0 20px', height: '45px', fontWeight: '800' }}
                        >
                          {searchingAcreedor ? '⏳' : 'CONSULTAR'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="form-group" style={{ marginTop: '12px', marginBottom: '0' }}>
                    <label>NOMBRE O RAZÓN SOCIAL DEL ACREEDOR <span style={{ color: '#94a3b8', fontSize: '11px' }}>(Opcional)</span></label>
                    <input 
                      type="text" 
                      name="acreedor_nombre" 
                      value={formData.acreedor_nombre} 
                      onChange={handleInputChange} 
                      placeholder="Nombre o razón social del acreedor prendario" 
                    />
                  </div>
                </div>
              )}

              <div className="form-section">
                <div className="form-section-header">
                  <div className="form-section-icon">📂</div>
                  <div>
                    <h4 className="form-section-title">DOCUMENTACIÓN DE SOPORTE</h4>
                    <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '2px', fontWeight: 'bold' }}>
                      ⚠️ ÚNICAMENTE SE ACEPTAN ARCHIVOS <span style={{ color: '#e11d48' }}>PDF O IMÁGENES</span>. (No Word / No Excel)
                    </span>
                  </div>
                </div>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      {formData.tipo_documento_id === 'NIT'
                        ? 'CÁMARA DE COMERCIO / RUT (PDF/IMG)'
                        : 'ID PERSONA NATURAL / REGISTRO MERCANTIL PERSONA JURÍDICA (PDF/IMG)'}
                      {' '}<span style={{ color: 'red' }}>*</span>
                    </label>
                    {formData.tipo_documento_id === 'NIT' && (
                      <small style={{ color: '#2563eb', display: 'block', marginTop: '2px', fontWeight: 'bold' }}>
                        ⚠️ Para NIT solo se carga Cámara de Comercio / RUT como documento de identificación.
                      </small>
                    )}
                    <input type="file" name="cedula" onChange={handleFileChange} className="file-input" accept=".pdf,image/*" required />
                  </div>
                  <div className="form-group">
                    <label>FACTURA (PDF/IMG)</label>
                    <input type="file" name="factura" onChange={handleFileChange} className="file-input" accept=".pdf,image/*" />
                  </div>
                </div>

                <div className="form-group">
                  <label>DECLARACIÓN DE IMPORTACIÓN / MANIFIESTO (PDF/IMG)</label>
                  <input type="file" name="declaracion_importacion" onChange={handleFileChange} className="file-input" accept=".pdf,image/*" />
                </div>

                {isPrenda && (
                  <div className="form-group">
                    <label>CONTRATO DE PRENDA (PDF/IMG) <span style={{ color: '#94a3b8', fontSize: '11px' }}>(Opcional)</span></label>
                    <input type="file" name="contrato_prenda" onChange={handleFileChange} className="file-input" accept=".pdf,image/*" />
                  </div>
                )}

                <div className="form-group" style={{ marginBottom: '0' }}>
                  <label>OBSERVACIONES</label>
                  <textarea name="observaciones" value={formData.observaciones} onChange={handleInputChange} rows="2" placeholder="detalles adicionales..." />
                </div>
              </div>

              <div className="form-section">
                <div className="form-section-header">
                  <div className="form-section-icon">🚗</div>
                  <h4 className="form-section-title">DETALLES DEL VEHÍCULO</h4>
                </div>
                
                <div className="form-group" style={{ marginBottom: '0' }}>
                  <label>
                    PLACA DE REFERENCIA {transitoConfig?.plate_assignment_mode === 'automatico' ? '(AUTOMÁTICA)' : '(MANUAL)'}
                    {transitoConfig?.plate_assignment_mode === 'manual' && <span style={{ color: 'var(--danger-color, #e74c3c)', marginLeft: '4px' }}>*</span>}</label>
                  <div className="searchable-select-container">
                    <input
                      type="text"
                      name="placa_referencia"
                      placeholder={
                        !formData.transito_id ? "seleccione primero un tránsito" :
                        transitoConfig?.plate_assignment_mode === 'automatico' ? "se asignará automáticamente..." :
                        "buscar placa disponible..."
                      }
                      value={searchTerm}
                      onChange={(e) => {
                        if (transitoConfig?.plate_assignment_mode === 'automatico') return;
                        // Lo escrito solo filtra la lista: la placa queda elegida únicamente al hacer clic
                        // en una opción (handlePlateSelect). Así no se puede enviar una placa escrita a mano.
                        setSearchTerm(e.target.value.toUpperCase());
                        setFormData({ ...formData, placa_referencia: '' });
                        setShowPlateDropdown(true);
                      }}
                      onBlur={() => {
                        // Si no se eligió ninguna opción, limpiar el texto para que no parezca seleccionada.
                        setTimeout(() => {
                          setShowPlateDropdown(false);
                          setFormData(prev => {
                            if (!prev.placa_referencia) setSearchTerm('');
                            return prev;
                          });
                        }, 200);
                      }}
                      onFocus={() => {
                        if (transitoConfig?.plate_assignment_mode === 'manual' && formData.transito_id) {
                          setShowPlateDropdown(true);
                        }
                      }}
                      autoComplete="off"
                      disabled={!formData.transito_id || transitoConfig?.plate_assignment_mode === 'automatico'}
                      style={transitoConfig?.plate_assignment_mode === 'automatico' ? { background: 'var(--secondary-soft)', color: 'var(--text-secondary)', fontWeight: '700' } : {}}
                    />
                    {showPlateDropdown && transitoConfig?.plate_assignment_mode === 'manual' && formData.transito_id && (
                      <div className="searchable-dropdown">
                        {loadingPlates ? (
                          <div className="dropdown-no-results" style={{ padding: '14px', textAlign: 'center', color: 'var(--primary-color)', fontWeight: '700', fontSize: '13px' }}>
                            <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', marginRight: '8px' }}>⏳</span>
                            Cargando placas disponibles...
                          </div>
                        ) : filteredPlates.length > 0 ? (
                          filteredPlates.map(p => (
                            <div key={p.id} className="dropdown-item" onClick={() => handlePlateSelect(p.plate_number)}>
                              <span className="plate-badge-sm">{p.plate_number}</span>
                              <small>({p.vehicle_type})</small>
                            </div>
                          ))
                        ) : (
                          <div className="dropdown-no-results">no hay placas disponibles</div>
                        )}
                      </div>
                    )}
                  </div>
                  {transitoConfig?.plate_assignment_mode === 'automatico' && (
                    <small style={{ color: 'var(--primary-color)', fontWeight: '600', marginTop: '4px', display: 'block' }}>
                      ℹ️ el sistema seleccionará la siguiente placa disponible para este tránsito.
                    </small>
                  )}
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>NÚMERO DE CHASIS</label>
                    <input 
                      type="text" 
                      name="chasis" 
                      value={formData.chasis} 
                      onChange={handleInputChange} 
                      placeholder="Número de chasis" 
                      style={formData.chasis ? { backgroundColor: '#e8f5e9', borderColor: '#4caf50' } : {}} 
                    />
                  </div>
                  <div className="form-group">
                    <label>NÚMERO DE MOTOR</label>
                    <input 
                      type="text" 
                      name="motor" 
                      value={formData.motor} 
                      onChange={handleInputChange} 
                      placeholder="Número de motor" 
                      style={formData.motor ? { backgroundColor: '#e8f5e9', borderColor: '#4caf50' } : {}} 
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>NÚMERO DE VIN</label>
                  <input 
                    type="text" 
                    name="vin" 
                    value={formData.vin} 
                    onChange={handleInputChange} 
                    placeholder="Número de VIN" 
                    style={formData.vin ? { backgroundColor: '#e8f5e9', borderColor: '#4caf50' } : {}} 
                  />
                </div>
              </div>

              <div className="modal-actions" style={{ padding: '0 20px 20px 20px', marginTop: '0' }}>
                <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>cancelar</button>
                <button type="submit" className="btn-primary" disabled={loading} style={{ padding: '10px 25px', fontSize: '13px', fontWeight: '800' }}>
                  {loading ? '🚀 ENVIANDO...' : '🚀 INICIAR TRÁMITE'}
                </button>
              </div>
            </>
          )}
          </>)}
        </form>
      </div>
    </div>
  );
};

export default NuevoTramiteModal;
